
import Link from '@/components/compat/Link';
import { BookMarked, Clock, Eye, Flame } from 'lucide-react';
import { PainelStatCard } from '@/components/painel/PainelStatCard';
import { painelTableShellClass } from '@/components/painel/painel-styles';
import { cn } from '@/lib/utils';
import { useAnalyticsGuides } from '@/hooks/use-analytics-guides';
import { AnalyticsPageChrome, AnalyticsPanel, AnalyticsPeriodBadge } from '@/components/painel/analytics/AnalyticsPageChrome';
import { usePainelText } from '@/components/painel/painel-texts-context';

function formatInt(n: number) {
  return new Intl.NumberFormat('pt-BR').format(Math.round(n));
}

function formatMin(n: number) {
  if (!n) return '—';
  const m = Math.floor(n);
  const s = Math.round((n - m) * 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

export function PainelAnalyticsGuiasPage() {
  const pageText = usePainelText('analytics_guias');
  const { data, loading } = useAnalyticsGuides();

  const sorted = data.guides;
  const top = data.topGuides.length ? data.topGuides : sorted.slice(0, 4);

  return (
    <AnalyticsPageChrome
      title={pageText.title}
      description={pageText.description}
      actions={<AnalyticsPeriodBadge>Últimos 7 dias</AnalyticsPeriodBadge>}
    >
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <PainelStatCard
          title="Guias publicados"
          value={loading ? '—' : formatInt(data.publishedCount)}
          icon={BookMarked}
          description="No CMS"
          variant="sky"
        />
        <PainelStatCard
          title="Visualizações"
          value={loading ? '—' : formatInt(data.totalViews)}
          icon={Eye}
          trend={data.viewsTrend ?? undefined}
          variant="cyan"
        />
        <PainelStatCard
          title="Melhor CTR"
          value={data.bestCtrLabel}
          icon={Flame}
          description={data.bestCtrTitle}
          variant="indigo"
        />
        <PainelStatCard
          title="Tempo médio"
          value={data.avgReadLabel}
          icon={Clock}
          description="Leitura média"
          variant="violet"
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <AnalyticsPanel
          title="Artigos em destaque"
          subtitle="Ordenados por visualizações"
        >
          <ul className="space-y-3">
            {top.map((row, i) => (
              <li
                key={row.id}
                className={cn(
                  'flex items-start gap-3 rounded-xl border border-white/[0.07] bg-white/[0.03] p-4',
                  'transition-colors hover:border-sky-400/25'
                )}
              >
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-sky-500/15 font-poppins text-xs font-bold text-sky-200">
                  {i + 1}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="font-poppins text-sm font-semibold text-white">{row.title}</p>
                  <p className="mt-0.5 font-inter text-xs text-slate-400/80">{row.categoryName}</p>
                  <div className="mt-2 flex flex-wrap gap-3 font-inter text-[11px] text-slate-400">
                    <span>{formatInt(row.views)} visualizações</span>
                    <span>{row.ctr}% CTR</span>
                    <span>{formatMin(row.avgMin)} méd.</span>
                  </div>
                </div>
                <Link
                  href={`/painel/guias/${row.id}/edit`}
                  className="shrink-0 self-center font-poppins text-[11px] font-semibold text-sky-300/90 hover:text-sky-200"
                >
                  Editar
                </Link>
              </li>
            ))}
          </ul>
          {loading ? (
            <p className="mt-3 font-inter text-xs text-slate-400/80">Carregando artigos…</p>
          ) : sorted.length === 0 ? (
            <p className="mt-3 font-inter text-xs text-slate-400/80">
              Ainda não há guias. Crie o primeiro em Guias no menu.
            </p>
          ) : null}
        </AnalyticsPanel>

        <AnalyticsPanel title="Resumo por métrica" subtitle="Como interpretar esses números no painel">
          <ul className="space-y-4 font-inter text-sm leading-relaxed text-slate-400">
            <li>
              <span className="font-semibold text-sky-200/90">Visualizações</span> — tráfego agregado ao artigo; útil para
              priorizar atualizações.
            </li>
            <li>
              <span className="font-semibold text-sky-200/90">CTR</span> — cliques vindos da busca e das listagens; exige
              eventos de busca + abertura instrumentados.
            </li>
            <li>
              <span className="font-semibold text-sky-200/90">Tempo</span> — envolvimento médio; disponível quando a
              integração GA4 / eventos de engajamento estiver ativa.
            </li>
            <li>
              <span className="font-semibold text-sky-200/90">Hoje / mês</span> — {formatInt(data.viewsToday)} hoje ·{' '}
              {formatInt(data.viewsMonth)} nos últimos 30 dias.
            </li>
          </ul>
        </AnalyticsPanel>
      </div>

      <AnalyticsPanel title="Todos os guias" subtitle="Métricas reais de visualização">
        <div className={cn(painelTableShellClass, 'overflow-hidden')}>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px] border-separate border-spacing-0">
              <thead>
                <tr className="bg-white/[0.03] font-poppins text-[10px] font-semibold uppercase tracking-wider text-sky-200/50">
                  <th className="px-4 py-3 text-left">Título</th>
                  <th className="px-4 py-3 text-left">Categoria</th>
                  <th className="px-4 py-3 text-right">Visualizações</th>
                  <th className="px-4 py-3 text-right">CTR</th>
                  <th className="px-4 py-3 text-right">Tempo méd.</th>
                  <th className="px-4 py-3 text-right" />
                </tr>
              </thead>
              <tbody className="font-inter text-sm">
                {sorted.map((row) => (
                  <tr key={row.id} className="text-blue-100/90">
                    <td className="border-t border-white/[0.06] px-4 py-3">
                      <span className="line-clamp-2">{row.title}</span>
                    </td>
                    <td className="border-t border-white/[0.06] px-4 py-3 text-slate-400">
                      {row.categoryName}
                    </td>
                    <td className="border-t border-white/[0.06] px-4 py-3 text-right tabular-nums text-slate-300">
                      {formatInt(row.views)}
                    </td>
                    <td className="border-t border-white/[0.06] px-4 py-3 text-right tabular-nums text-slate-300">
                      {row.ctr}%
                    </td>
                    <td className="border-t border-white/[0.06] px-4 py-3 text-right tabular-nums text-slate-300">
                      {formatMin(row.avgMin)}
                    </td>
                    <td className="border-t border-white/[0.06] px-4 py-3 text-right">
                      <Link
                        href={`/painel/guias/${row.id}/edit`}
                        className="font-poppins text-xs font-semibold text-sky-300/90 hover:text-sky-200"
                      >
                        Editar
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </AnalyticsPanel>
    </AnalyticsPageChrome>
  );
}
